import { useCallback, useMemo, useRef, useEffect } from 'react';
import { useFormContext } from '../../../../../contexts/useFormContext';
import { createLogger } from '../../../../../utils/logger';
import { SectionKey } from '../../../../../types';
import { FormDataType } from '@tw/tw-component-library';
import { DEFAULT_CONFIG } from '../../constants/formConstants';
import { isDeepEqual } from '../../utils/comparison';
import { handleFormError } from '../../utils/errorHandling';
import { useFormValidation } from '../validation/useFormValidation';
import { useFormCalculations } from '../calculations/useFormCalculations';
import { useDependencyProcessor } from '../dependencies/useDependencyProcessing';
import { useSchemas } from '../schemas/useSchemas';
import { useUnifiedSchemaProcessing } from '../schemas/useUnifiedSchemaProcessing';
import { useFormRefs } from '../config/useFormRefs';
import { useFormHandlers } from '../useFormHandlers';
import { focusErrorField } from '../../../../../utils/focusErrorField';
import {
  FormConfigOptions,
  FormSchemaConfig,
  ValidationResult,
  EnhancedFieldConfig,
  EnhancedSection,
  EnhancedGroupedSection,
} from '../../types';
import { UseAuth } from '../../../../../contexts/AuthContext';
import { isDummyOvertimeWorkflowId } from '../../schemas/OvertimeApproval/dummyWorkflow';
import {
  isTestingDummyWorkflowId,
  shouldBypassWorkflowBackend,
} from '../../../../../utils/workflowTestingBypass';

const logger = createLogger('useFormManager');

/**
 * Unified form manager hook that consolidates functionality from multiple hooks
 *
 * @param config - Form configuration options
 * @returns Consolidated form management functions and state
 */
export const useFormManager = (config: Partial<FormConfigOptions> = {}) => {
  const {
    formState,
    updateFormData,
    setErrors,
    completeSection,
    setCurrentSection,
    canProceedToSection,
    sectionProgress,
    updateSectionState,
  } = useFormContext();
  const { getAccessToken, spoofedUser } = UseAuth();

  const bypassBackend =
    shouldBypassWorkflowBackend(formState.workflowType) ||
    isTestingDummyWorkflowId(formState.data?.id) ||
    isDummyOvertimeWorkflowId(formState.data?.id);

  // Merge configuration with defaults (force validation off in testing bypass)
  const formConfig = useMemo(
    () => ({
      ...DEFAULT_CONFIG,
      ...config,
      ...(bypassBackend ? { enableValidation: false } : {}),
    }),
    [config, bypassBackend]
  );

  // Initialize form handlers
  const { onSubmit: submitFormHandler } = useFormHandlers({
    // Testing / temporary overtime: mock submit only — no workflow APIs.
    bypassSubmit: bypassBackend,
    onSubmitSuccess: () => {
      // Only log if debugMode is enabled
      if (formConfig.debugMode) {
        logger.debug('Form submitted successfully');
      }
    },
    onSubmitError: (error: Error) => {
      // Only log if debugMode is enabled
      if (formConfig.debugMode) {
        logger.error('Form submission error', {
          errorMessage: error.message,
        });
      }
      // Add to existing errors instead of overwriting
      setErrors({ _form: error.message });
    },
    getAccessToken,
    spoofedUser: spoofedUser?.value || null,
  });

  // Track previous values for efficient comparison
  const prevValues = useRef({
    formData: formState.data,
    currentSection: formState.currentSection,
    config: formConfig,
  });

  // Check for significant changes using deep comparison
  useEffect(() => {
    const hasDataChanged = !isDeepEqual(
      prevValues.current.formData,
      formState.data
    );
    const hasSectionChanged =
      prevValues.current.currentSection !== formState.currentSection;
    const hasConfigChanged = !isDeepEqual(
      prevValues.current.config,
      formConfig
    );

    if (hasDataChanged || hasSectionChanged || hasConfigChanged) {
      prevValues.current = {
        formData: formState.data,
        currentSection: formState.currentSection,
        config: formConfig,
      };

      if (formConfig.debugMode) {
        logger.state('Form manager detected changes', {
          hasDataChanged,
          hasSectionChanged,
          hasConfigChanged,
          validationEnabled: formConfig.enableValidation,
          calculationsEnabled: formConfig.enableCalculations,
          dependenciesEnabled: formConfig.enableDependencyProcessing,
        });
      }
    }
  }, [formState.data, formState.currentSection, formConfig]);

  // Initialize specialized hooks - always call hooks but conditionally use results
  const refs = useFormRefs(formState);
  const validationHook = useFormValidation();
  const calculationsHook = useFormCalculations();
  const dependencyHook = useDependencyProcessor(formState.data);
  const schemas = useSchemas();

  // Use hook results based on config
  const validateSection = useCallback(
    (schema: FormSchemaConfig, data: FormDataType) => {
      if (!formConfig.enableValidation) {
        return { isValid: true, errors: [] };
      }

      return validationHook.validateSection(schema, data);
    },
    [formConfig.enableValidation, validationHook]
  );

  const processCalculations = useCallback(
    (schema: FormSchemaConfig, data: FormDataType) => {
      if (!formConfig.enableCalculations) {
        return {};
      }
      return calculationsHook.processCalculations(schema, data);
    },
    [formConfig.enableCalculations, calculationsHook]
  );

  // Extract dependency processing functions with proper memoization
  // Using a stable reference pattern to prevent stale closures
  const dependencyFunctions = useMemo(() => {
    if (!formConfig.enableDependencyProcessing || !dependencyHook) {
      return {
        processFields: (fields: EnhancedFieldConfig[]) => fields,
        processSections: (
          sections: (EnhancedSection | EnhancedGroupedSection)[]
        ) => sections,
        evaluateDependency: () => true,
        getDependentFields: () => [],
        queuePendingUpdate: () => {},
      };
    }
    return {
      processFields: dependencyHook.processFields,
      processSections: dependencyHook.processSections,
      evaluateDependency: dependencyHook.evaluateDependency,
      getDependentFields: dependencyHook.getDependentFields,
      queuePendingUpdate: dependencyHook.queuePendingUpdate || (() => {}),
    };
  }, [formConfig.enableDependencyProcessing, dependencyHook]);

  // CRITICAL: Wrap updateFormData to also immediately update dependency processor ref
  // This ensures dependency processing sees field changes before React re-renders
  const updateFormDataWithDependencySync = useCallback(
    (update: Partial<typeof formState> | Record<string, unknown>) => {
      // Immediately sync to dependency processor's ref so processFields can see it
      if (
        formConfig.enableDependencyProcessing &&
        dependencyFunctions.queuePendingUpdate
      ) {
        // Extract just the data part if it's a FormState update
        const dataUpdate: Partial<FormDataType> =
          'data' in update && update.data
            ? update.data
            : (update as Partial<FormDataType>);
        dependencyFunctions.queuePendingUpdate(dataUpdate);
      }

      // Then update the actual state
      updateFormData(update);
    },
    [updateFormData, formConfig.enableDependencyProcessing, dependencyFunctions]
  );

  const {
    processFields,
    processSections,
    evaluateDependency,
    getDependentFields: getDependentFieldsFromProcessor,
  } = dependencyFunctions;

  // Adapter function to match the expected signature
  const getDependentFieldsAdapter = useCallback(
    (fieldName: string, schema: FormSchemaConfig): string[] => {
      return getDependentFieldsFromProcessor(fieldName, schema.sections);
    },
    [getDependentFieldsFromProcessor]
  );

  // Use unified schema processing
  const { processSchema } = useUnifiedSchemaProcessing({
    formData: formState.data,
    schemas: schemas as unknown as Record<SectionKey, FormSchemaConfig>,
    completedSections: formState.completedSections,
    formConfig,
    refs,
    processDependencies: processFields,
    getDependentFields: getDependentFieldsAdapter,
    validateDependency: evaluateDependency,
    formState,
  });

  // Get schema for a specific section
  const getSectionSchema = useCallback(
    (section: SectionKey): FormSchemaConfig => {
      const schema = schemas[section as keyof typeof schemas];
      if (!schema) {
        // Only log if debugMode is enabled
        if (formConfig.debugMode) {
          logger.warning(`Schema not found for section: ${section}`);
        }
        return { sections: [] };
      }
      return schema;
    },
    [schemas, formConfig.debugMode]
  );

  // Trigger calculations when rows or direct payment_total change
  const grid = formState.data?.gridData;
  const rows = Array.isArray(grid?.data)
    ? grid.data
    : Array.isArray(grid)
    ? grid
    : Array.isArray(grid?.payment_details)
    ? grid.payment_details
    : [];
  const gridDataString = JSON.stringify(rows);
  const paymentTotalKey = String(formState.data?.payment_total ?? '');
  useEffect(() => {
    // Run when either grid rows change (1-5 payments) OR payment_total changes (5+ payments)
    if (
      formConfig.enableCalculations &&
      (formState.data?.gridData || formState.data?.payment_total !== undefined)
    ) {
      const currentSchema = getSectionSchema(formState.currentSection);
      if (currentSchema) {
        const calculatedData = processCalculations(
          currentSchema,
          formState.data
        );
        // Only update if the calculated value is different
        const keys = Object.keys(calculatedData);
        let shouldUpdate = false;
        for (const key of keys) {
          if (!isDeepEqual(formState.data[key], calculatedData[key])) {
            shouldUpdate = true;
            break;
          }
        }
        if (shouldUpdate) {
          updateFormData(calculatedData);
          if (formConfig.debugMode) {
            logger.debug(
              'Calculations triggered by gridData/payment_total change',
              calculatedData
            );
          }
        }
      }
    }
    // eslint-disable-next-line
  }, [
    gridDataString,
    paymentTotalKey,
    formConfig.enableCalculations,
    formState.currentSection,
    getSectionSchema,
    processCalculations,
    updateFormData,
    formConfig.debugMode,
  ]);

  // Handle form validation
  const handleValidation = useCallback(
    (schema: FormSchemaConfig, data: FormDataType): ValidationResult => {
      try {
        if (!formConfig.enableValidation || bypassBackend) {
          return { isValid: true, errors: [] };
        }
        return validateSection(schema, data);
      } catch (error) {
        const formattedError = handleFormError(error, 'validation');
        // Only log if debugMode is enabled
        if (formConfig.debugMode) {
          logger.error('Validation error:', formattedError);
        }
        return {
          isValid: false,
          errors: [{ field: '_form', message: formattedError.message }],
        };
      }
    },
    [
      formConfig.enableValidation,
      formConfig.debugMode,
      validateSection,
      bypassBackend,
    ]
  );

  // Handle form calculations
  const handleCalculations = useCallback(
    async (
      schema: FormSchemaConfig,
      data: FormDataType
    ): Promise<Partial<FormDataType>> => {
      try {
        if (!formConfig.enableCalculations) {
          return {};
        }
        return processCalculations(schema, data);
      } catch (error) {
        const formattedError = handleFormError(error, 'calculations');
        // Only log if debugMode is enabled
        if (formConfig.debugMode) {
          logger.error('Calculation error:', formattedError);
        }
        return {};
      }
    },
    [formConfig.enableCalculations, formConfig.debugMode, processCalculations]
  );

  // Process form data with all enhancements
  const processFormData = useCallback(
    async (
      schema: FormSchemaConfig,
      data: FormDataType
    ): Promise<FormDataType> => {
      try {
        let processedData = { ...data };

        // Apply calculations if enabled
        if (formConfig.enableCalculations) {
          const calculatedData = await handleCalculations(
            schema,
            processedData
          );
          processedData = { ...processedData, ...calculatedData };
        }

        return processedData;
      } catch (error) {
        const formattedError = handleFormError(error, 'processing');
        // Only log if debugMode is enabled
        if (formConfig.debugMode) {
          logger.error('Data processing error:', formattedError);
        }
        return data;
      }
    },
    [formConfig.enableCalculations, formConfig.debugMode, handleCalculations]
  );

  // Validate and process section
  const validateAndProcessSection = useCallback(
    async (
      section: SectionKey,
      data: FormDataType
    ): Promise<{
      isValid: boolean;
      processedData: FormDataType;
      errors: ValidationResult['errors'];
    }> => {
      try {
        const schema = getSectionSchema(section);
        const processedSchema = processSchema(schema, data, section);

        // Validate the section
        const validationResult = handleValidation(processedSchema, data);

        // Process the data
        const processedData = await processFormData(processedSchema, data);

        return {
          isValid: validationResult.isValid,
          processedData,
          errors: validationResult.errors || [],
        };
      } catch (error) {
        const formattedError = handleFormError(error, 'section-processing');
        // Only log if debugMode is enabled
        if (formConfig.debugMode) {
          logger.error('Section processing error:', formattedError);
        }
        return {
          isValid: false,
          processedData: data,
          errors: [{ field: '_form', message: formattedError.message }],
        };
      }
    },
    [
      getSectionSchema,
      processSchema,
      handleValidation,
      processFormData,
      formConfig.debugMode,
    ]
  );

  // Get current form schema
  // Create a hash of key dependency fields to force recalculation when they change
  // This avoids infinite loops while ensuring schema updates when dependencies change
  // The hash is included in the dependency array to trigger recalculation
  const dependencyFieldsHash = useMemo(() => {
    if (!formState.data) return '';
    // Create a hash of fields commonly used in dependencies
    const keyFields = [
      'payments_count',
      'interest',
      'interest_options',
      'payment_total',
      'confirm_approval_overtime_request',
    ];
    const values = keyFields.map((field) => formState.data?.[field]).join('|');
    return values;
  }, [
    formState.data?.payments_count,
    formState.data?.interest,
    formState.data?.interest_options,
    formState.data?.payment_total,
    formState.data?.confirm_approval_overtime_request,
  ]);

  const currentFormSchema = useMemo(() => {
    if (!formState.currentSection) return null;
    const schema = getSectionSchema(formState.currentSection);
    return formState.data
      ? processSchema(schema, formState.data, formState.currentSection)
      : schema;
  }, [
    formState.currentSection,
    formState.data,
    getSectionSchema,
    processSchema,
    // Include dependencyFieldsHash to force recalculation when dependency-affecting fields change
    // This ensures schema updates when dependencies change visibility, without causing loops
    dependencyFieldsHash,
  ]);

  // Handle form submission
  const handleSubmit = useCallback(
    async (section: SectionKey, data: FormDataType) => {
      try {
        // 1. Always validate and process the section first

        const result = await validateAndProcessSection(section, data);

        if (!result.isValid) {
          // Set errors and return failure
          const errorMap = result.errors.reduce((acc, error) => {
            acc[error.field || '_form'] = error.message;
            return acc;
          }, {} as Record<string, string>);
          setErrors(errorMap);

          // Focus on the first error field
          focusErrorField(errorMap, section);

          return result;
        }

        // 2. Update form data with processed data
        updateFormData(result.processedData);

        // 3. Submit via the workflow handler (this is the ONLY place it's called)
        const submitSuccess = await submitFormHandler();

        if (submitSuccess) {
          // Clear any previous errors on success
          setErrors({});
          return { ...result, isValid: true };
        } else {
          if (formConfig.debugMode) {
            logger.error('Form submission failed');
          }
          // Don't set a generic error - let useFormHandlers handle specific errors
          return { ...result, isValid: false };
        }
      } catch (error) {
        const formattedError = handleFormError(error, 'submission');
        if (formConfig.debugMode) {
          logger.error('Form submission error', { error: formattedError });
        }
        // Add to existing errors instead of overwriting
        setErrors({ _form: formattedError.message });
        return {
          isValid: false,
          processedData: data,
          errors: [{ field: '_form', message: formattedError.message }],
        };
      }
    },
    [
      validateAndProcessSection,
      updateFormData,
      submitFormHandler,
      setErrors,
      formConfig.debugMode,
    ]
  );
  //   if (formConfig.enableDependencyProcessing && formState.currentSection) {
  //     // Force re-evaluation of dependencies when formState.data changes
  //     const currentSchema = getSectionSchema(formState.currentSection);
  //     const updatedSchema = processSchema(
  //       currentSchema,
  //       formState.data,
  //       formState.currentSection
  //     );

  //     // Important: Update the form data with any changes from dependency processing
  //     // This ensures visibility changes are properly applied
  //     const processedData = { ...formState.data };

  //     // Apply visibility changes to the form data
  //     updatedSchema.sections.forEach((section) => {
  //
  //       if (isEnhancedGroupedSection(section)) {
  //         section.sections.forEach((subSection) => {
  //           subSection.fields.forEach(
  //             (field: { name: string | number; hidden: undefined }) => {
  //               if (field.name && field.hidden !== undefined) {
  //                 // Set the hidden property on the appropriate field in form data
  //                 if (!processedData[field.name]) {
  //                   processedData[field.name] = {};
  //                 }
  //                 processedData[field.name].hidden = field.hidden;
  //               }
  //             }
  //           );
  //         });
  //       } else {
  //         section.fields.forEach((field) => {
  //           if (field.name && field.hidden !== undefined) {
  //             // Set the hidden property on the appropriate field in form data
  //             if (!processedData[field.name]) {
  //               processedData[field.name] = {};
  //             }
  //             processedData[field.name].hidden = field.hidden;
  //           }
  //         });
  //       }
  //     });

  //     // Only update if there are actual changes to avoid loops
  //     if (!isDeepEqual(processedData, formState.data)) {
  //       updateFormData(processedData);

  //       if (formConfig.debugMode) {
  //         logger.debug('Updated form data with processed dependencies', {
  //           changedFields: Object.keys(processedData).filter(
  //             (key) => processedData[key] !== formState.data?.[key]
  //           ),
  //         });
  //       }
  //     }
  //   }
  // }, [
  //   formState.data,
  //   formState.currentSection,
  //   formConfig.enableDependencyProcessing,
  //   getSectionSchema,
  //   processSchema,
  //   updateFormData,
  //   formConfig.debugMode,
  // ]);

  return {
    // State
    formState,
    formConfig,
    schemas,
    sectionProgress,
    currentFormSchema,

    // Core functions
    updateFormData: updateFormDataWithDependencySync,
    setErrors,
    completeSection,
    setCurrentSection,
    canProceedToSection,
    updateSectionState,
    handleSubmit,

    // Enhanced functions
    getSectionSchema,
    processSchema,
    handleValidation,
    handleCalculations,
    processFormData,
    validateAndProcessSection,

    // Dependency functions
    processFields,
    processSections,
    getDependentFields: getDependentFieldsAdapter,
    evaluateDependency,

    // Refs
    refs,
  };
};
